import { useState, useMemo, useCallback } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable,
} from '@dnd-kit/core';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  ChevronLeft, ChevronRight, Facebook, Instagram,
  Clock, Send, Edit as EditIcon, Film, Image as ImageIcon, GripHorizontal
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths, parseISO, setHours, setMinutes } from 'date-fns';
import { it } from 'date-fns/locale';
import TikTokIcon from '../icons/TikTokIcon';

const platformIcons = { facebook: Facebook, instagram: Instagram, tiktok: TikTokIcon };
const platformColors = { facebook: 'bg-blue-600', instagram: 'bg-pink-500', tiktok: 'bg-black' };
const statusDot = { draft: 'bg-gray-400', scheduled: 'bg-blue-500', published: 'bg-emerald-500', failed: 'bg-red-500', pending_approval: 'bg-amber-500' };
const statusLabels = { draft: 'Bozza', scheduled: 'Programmato', published: 'Pubblicato', failed: 'Fallito', pending_approval: 'In approvazione' };
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

// ─── Draggable Post Pill ───
const DraggablePost = ({ post }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: post.id, data: { post } });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      title="Trascina per spostare"
      className={`group/drag flex items-center gap-1 px-1 py-0.5 rounded border transition-all touch-none ${
        isDragging
          ? 'opacity-30 border-primary'
          : 'bg-card/80 border-border/50 hover:bg-primary/5 hover:border-primary/40 hover:shadow-sm cursor-grab active:cursor-grabbing'
      }`}
      data-testid={`draggable-post-${post.id}`}
    >
      <GripHorizontal className="w-2.5 h-2.5 text-muted-foreground/30 group-hover/drag:text-primary/60 flex-shrink-0 transition-colors" />
      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot[post.post_status]}`} />
      {post.platforms.slice(0, 2).map(p => {
        const Icon = platformIcons[p];
        return Icon ? <Icon key={p} className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" /> : null;
      })}
      <span className="text-[9px] text-muted-foreground truncate leading-none flex-1">{post.content.slice(0, 18)}</span>
    </div>
  );
};

// ─── Droppable Day Cell ───
const DroppableDay = ({ dateKey, day, isCurrentMonth, isToday, isSelected, dayPosts, onClick }) => {
  const { isOver, setNodeRef } = useDroppable({ id: dateKey });
  const dotColors = dayPosts.slice(0, 4).map(p => statusDot[p.post_status]);

  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      className={`
        relative min-h-[52px] md:min-h-[100px] p-1 md:p-1.5 border-b border-r text-left transition-all cursor-pointer
        ${!isCurrentMonth ? 'bg-muted/20 text-muted-foreground/40' : 'hover:bg-muted/30'}
        ${isSelected ? 'bg-primary/5 ring-2 ring-inset ring-primary/30' : ''}
        ${isOver ? 'bg-primary/10 ring-2 ring-inset ring-primary/50' : ''}
      `}
      data-testid={`cal-day-${dateKey}`}
    >
      {/* Day number */}
      <span className={`inline-flex items-center justify-center w-5 h-5 md:w-6 md:h-6 rounded-full text-[11px] md:text-xs font-medium ${isToday ? 'bg-primary text-primary-foreground' : ''}`}>
        {format(day, 'd')}
      </span>

      {/* Mobile: compact dot indicators (up to 4 dots) */}
      {dayPosts.length > 0 && isCurrentMonth && (
        <div className="md:hidden absolute bottom-1 left-1 right-1 flex items-center justify-center gap-0.5 flex-wrap">
          {dotColors.map((c, i) => (
            <span key={i} className={`w-1.5 h-1.5 rounded-full ${c}`} />
          ))}
          {dayPosts.length > 4 && (
            <span className="text-[8px] text-muted-foreground leading-none">+{dayPosts.length - 4}</span>
          )}
        </div>
      )}

      {/* Desktop: draggable post pills */}
      {dayPosts.length > 0 && isCurrentMonth && (
        <div className="hidden md:block mt-1 space-y-0.5">
          {dayPosts.slice(0, 3).map(post => (
            <DraggablePost key={post.id} post={post} />
          ))}
          {dayPosts.length > 3 && (
            <span className="text-[9px] text-muted-foreground px-1">+{dayPosts.length - 3} altri</span>
          )}
        </div>
      )}

      {/* Drop indicator when dragging over */}
      {isOver && (
        <div className="absolute inset-0 border-2 border-dashed border-primary/50 rounded-lg pointer-events-none" />
      )}
    </div>
  );
};

// ─── Drag Overlay (ghost that follows cursor) ───
const DragOverlayContent = ({ post }) => (
  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-card border-2 border-primary shadow-lg min-w-[140px]">
    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot[post.post_status]}`} />
    {post.platforms.slice(0, 2).map(p => {
      const Icon = platformIcons[p];
      return Icon ? <Icon key={p} className="w-3 h-3 text-muted-foreground flex-shrink-0" /> : null;
    })}
    <span className="text-xs text-foreground truncate">{post.content.slice(0, 30)}</span>
  </div>
);

// ─── Main Component ───
const SocialCalendarView = ({ posts, onEdit, onPublish, onReschedule, API }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activePost, setActivePost] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const start = startOfWeek(monthStart, { weekStartsOn: 1 });
    const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = [];
    let day = start;
    while (day <= end) { days.push(new Date(day)); day = addDays(day, 1); }
    return days;
  }, [currentMonth]);

  const postsByDate = useMemo(() => {
    const map = {};
    posts.forEach(post => {
      const dateStr = post.scheduled_time
        ? format(parseISO(post.scheduled_time), 'yyyy-MM-dd')
        : format(parseISO(post.created_at), 'yyyy-MM-dd');
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(post);
    });
    return map;
  }, [posts]);

  const selectedDayPosts = useMemo(() => {
    if (!selectedDate) return [];
    const key = format(selectedDate, 'yyyy-MM-dd');
    return postsByDate[key] || [];
  }, [selectedDate, postsByDate]);

  const today = new Date();

  const handleDragStart = useCallback((event) => {
    const post = event.active.data.current?.post;
    if (post) setActivePost(post);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActivePost(null);
    const { active, over } = event;
    if (!over || !active.data.current?.post) return;

    const post = active.data.current.post;
    const targetDateKey = over.id; // format: yyyy-MM-dd

    // Current date of the post
    const currentDateKey = post.scheduled_time
      ? format(parseISO(post.scheduled_time), 'yyyy-MM-dd')
      : format(parseISO(post.created_at), 'yyyy-MM-dd');

    if (currentDateKey === targetDateKey) return; // Same day, no change

    // Only allow rescheduling draft/scheduled/failed posts (not published)
    if (post.post_status === 'published') return;

    // Build new scheduled_time: keep existing time, change date
    const targetDate = parseISO(targetDateKey);
    let newScheduledTime;
    if (post.scheduled_time) {
      const existingTime = parseISO(post.scheduled_time);
      newScheduledTime = setHours(setMinutes(targetDate, existingTime.getMinutes()), existingTime.getHours());
    } else {
      newScheduledTime = setHours(setMinutes(targetDate, 0), 9); // Default 09:00
    }

    onReschedule(post.id, newScheduledTime.toISOString());
  }, [onReschedule]);

  const handleDragCancel = useCallback(() => {
    setActivePost(null);
  }, []);

  return (
    <div className="space-y-4" data-testid="social-calendar-view">
      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="cal-prev-month">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="text-center">
          <h2 className="text-lg font-semibold font-outfit capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: it })}
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            <span className="hidden md:inline">Trascina i post per riprogrammarli</span>
            <span className="md:hidden">Tocca un giorno per vedere i post</span>
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="cal-next-month">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Calendar Grid with DnD */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        <div className="border rounded-xl overflow-hidden">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 bg-muted/50">
            {WEEKDAYS.map(day => (
              <div key={day} className="px-2 py-2 text-center text-xs font-medium text-muted-foreground border-b">
                {day}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayPosts = postsByDate[dateKey] || [];
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isToday = isSameDay(day, today);
              const isSelected = selectedDate && isSameDay(day, selectedDate);

              return (
                <DroppableDay
                  key={dateKey}
                  dateKey={dateKey}
                  day={day}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isToday}
                  isSelected={isSelected}
                  dayPosts={dayPosts}
                  onClick={() => setSelectedDate(day)}
                />
              );
            })}
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
          {activePost ? <DragOverlayContent post={activePost} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Selected Day Detail Panel */}
      {selectedDate && (
        <div className="border rounded-xl overflow-hidden" data-testid="cal-day-detail">
          <div className="px-4 py-3 bg-muted/30 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              {format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}
            </h3>
            <Badge variant="outline" className="text-xs">{selectedDayPosts.length} post</Badge>
          </div>

          {selectedDayPosts.length > 0 ? (
            <ScrollArea className="max-h-[300px]">
              <div className="divide-y">
                {selectedDayPosts.map(post => (
                  <div key={post.id} className="p-3 hover:bg-muted/20 transition-colors" data-testid={`cal-post-${post.id}`}>
                    <div className="flex items-start gap-3">
                      {/* Media thumbnail */}
                      {post.media_urls?.length > 0 ? (
                        <div className="w-12 h-12 rounded-lg overflow-hidden border bg-muted flex-shrink-0 relative">
                          {(() => {
                            const media = post.media_urls[0];
                            const url = typeof media === 'string' ? media : media.url;
                            const isVideo = post.media_types?.[0] === 'video' || (typeof media === 'string' ? url.match(/\.(mp4|mov|webm|avi)$/i) : media.media_type === 'video');
                            const thumbUrl = post.thumbnails?.[0];
                            if (isVideo && thumbUrl) {
                              return (<>
                                <img src={`${API.replace('/api', '')}${thumbUrl}`} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20"><Film className="w-3.5 h-3.5 text-white drop-shadow" /></div>
                              </>);
                            }
                            if (isVideo) return <div className="w-full h-full flex items-center justify-center bg-black/10"><Film className="w-5 h-5 text-muted-foreground" /></div>;
                            return <img src={`${API.replace('/api', '')}${url}`} alt="" className="w-full h-full object-cover" />;
                          })()}
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-lg border bg-muted flex items-center justify-center flex-shrink-0">
                          <ImageIcon className="w-5 h-5 text-muted-foreground/30" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          {post.platforms.map(p => {
                            const Icon = platformIcons[p];
                            return Icon ? (
                              <div key={p} className={`w-5 h-5 rounded ${platformColors[p]} flex items-center justify-center`}>
                                <Icon className="w-3 h-3 text-white" />
                              </div>
                            ) : null;
                          })}
                          <Badge variant="outline" className="text-[10px] ml-1">{statusLabels[post.post_status]}</Badge>
                          {post.scheduled_time && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 ml-auto">
                              <Clock className="w-3 h-3" />
                              {format(parseISO(post.scheduled_time), 'HH:mm')}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-foreground line-clamp-2">{post.content}</p>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {(post.post_status === 'draft' || post.post_status === 'failed') && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onPublish(post.id)} data-testid={`cal-publish-${post.id}`}>
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(post)} data-testid={`cal-edit-${post.id}`}>
                          <EditIcon className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Nessun post per questa data
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SocialCalendarView;
